import React, { useEffect, useRef, useState, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, Send, Search, User as UserIcon, CheckCheck, Check } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch { return ""; }
}

function fmtDayLabel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const y = new Date(now); y.setDate(now.getDate() - 1);
    if (d.toDateString() === now.toDateString()) return "Oggi";
    if (d.toDateString() === y.toDateString()) return "Ieri";
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return ""; }
}

function initials(name) {
  return (name || "?").split(" ").filter(Boolean).map(p => p[0]).slice(0, 2).join("").toUpperCase();
}

export default function Messaggi() {
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const bottomRef = useRef(null);

  const loadThreads = async () => {
    try {
      const r = await api.get("/chat/threads");
      setThreads(r.data || []);
    } catch { /* noop */ }
  };

  const loadMessages = async (driverId) => {
    if (!driverId) { setMessages([]); return; }
    try {
      const r = await api.get("/chat/messages", { params: { driver_id: driverId } });
      setMessages(r.data || []);
      // mark as read (office side)
      api.post("/chat/mark-read", { driver_id: driverId }).then(loadThreads).catch(() => {});
    } catch { /* noop */ }
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { loadMessages(selectedId); }, [selectedId]);

  // Polling: threads every 5s, active messages every 3s
  useEffect(() => {
    const t = setInterval(loadThreads, 5000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!selectedId) return;
    const t = setInterval(() => loadMessages(selectedId), 3000);
    return () => clearInterval(t);
  }, [selectedId]);

  // Auto scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const filteredThreads = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return threads;
    return threads.filter(t => (t.driver_name || "").toLowerCase().includes(s));
  }, [threads, q]);

  const selected = threads.find(t => t.driver_id === selectedId);

  const send = async () => {
    const text = body.trim();
    if (!text || !selectedId) return;
    setSending(true);
    try {
      await api.post("/chat/messages", { driver_id: selectedId, body: text });
      setBody("");
      await loadMessages(selectedId);
      await loadThreads();
    } catch (e) {
      toast.error("Invio non riuscito");
    }
    setSending(false);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Group messages by day
  const grouped = useMemo(() => {
    const out = [];
    let currentDay = null;
    for (const m of messages) {
      const day = (m.created_at || "").slice(0, 10);
      if (day !== currentDay) {
        out.push({ type: "day", key: `day-${day}`, iso: m.created_at });
        currentDay = day;
      }
      out.push({ type: "msg", key: m.id, msg: m });
    }
    return out;
  }, [messages]);

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);

  return (
    <div className="space-y-4" data-testid="messaggi-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white flex items-center gap-3">
            <MessageSquare className="text-amber-400" size={28} /> Messaggi
          </h1>
          <div className="text-sm text-slate-400 mt-1">
            Chat diretta con gli autisti · aggiornamento automatico
            {totalUnread > 0 && <span className="ml-2 inline-flex items-center gap-1 text-rose-400 font-mono-num text-xs uppercase tracking-widest">· {totalUnread} non letti</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[70vh]">
        {/* Threads list */}
        <aside className="lg:col-span-4 xl:col-span-3 rounded-2xl border border-slate-800/80 bg-[#0A1428] flex flex-col overflow-hidden">
          <div className="p-3 border-b border-slate-800/80">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Cerca autista..."
                data-testid="chat-search-driver"
                className="pl-9 bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-500 h-9 text-sm"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" ref={listRef} data-testid="chat-threads-list">
            {filteredThreads.length === 0 && (
              <div className="p-6 text-center text-slate-500 text-sm italic">
                {q ? "Nessun autista trovato" : "Nessun autista disponibile"}
              </div>
            )}
            {filteredThreads.map(t => {
              const active = t.driver_id === selectedId;
              const last = t.last_message;
              const preview = last ? (last.body || "").slice(0, 60) : "Nessun messaggio";
              const lastRole = last?.sender_role;
              return (
                <button
                  key={t.driver_id}
                  onClick={() => setSelectedId(t.driver_id)}
                  data-testid={`chat-thread-${t.driver_id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-800/50 flex items-start gap-3 transition-colors ${active ? "bg-amber-500/10 border-l-2 border-l-amber-400" : "hover:bg-slate-900/60"}`}
                >
                  <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-sm ${active ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-slate-800 text-slate-300 border border-slate-700"}`}>
                    {initials(t.driver_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={`font-bold text-sm truncate ${active ? "text-amber-100" : "text-white"}`}>{t.driver_name}</span>
                      {last && <span className="text-[10px] text-slate-500 font-mono-num shrink-0">{fmtTime(last.created_at)}</span>}
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <span className="text-xs text-slate-400 truncate">
                        {lastRole && lastRole !== "autista" && <span className="text-slate-500">Tu: </span>}
                        {preview}
                      </span>
                      {t.unread > 0 && (
                        <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-slate-950 text-[10px] font-extrabold flex items-center justify-center" data-testid={`chat-unread-badge-${t.driver_id}`}>
                          {t.unread > 99 ? "99+" : t.unread}
                        </span>
                      )}
                    </div>
                    {!t.has_account && (
                      <div className="text-[9px] text-amber-500/70 mt-0.5 uppercase tracking-widest font-mono-num">Nessun account driver</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Conversation */}
        <section className="lg:col-span-8 xl:col-span-9 rounded-2xl border border-slate-800/80 bg-[#0A1428] flex flex-col overflow-hidden">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center flex-col gap-3 text-slate-500 p-8">
              <div className="w-16 h-16 rounded-2xl bg-slate-800/60 border border-slate-800 flex items-center justify-center">
                <MessageSquare size={28} className="text-slate-600" />
              </div>
              <div className="text-center">
                <div className="text-white font-bold text-lg">Seleziona un autista</div>
                <div className="text-sm text-slate-500 mt-1">Scegli un contatto per iniziare la conversazione</div>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-5 py-3 border-b border-slate-800/80 flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 font-bold flex items-center justify-center">
                  {initials(selected.driver_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold">{selected.driver_name}</div>
                  <div className="text-xs text-slate-500 font-mono-num">
                    {selected.driver_phone ? selected.driver_phone : "Autista"}
                    {!selected.has_account && <span className="ml-2 text-amber-400/80">· driver senza account (non riceverà i messaggi finché non attivi il login)</span>}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-5 space-y-2 bg-[#050B18]/40" data-testid="chat-messages-list">
                {messages.length === 0 && (
                  <div className="text-center text-slate-500 italic py-10 text-sm">Nessun messaggio ancora. Scrivi il primo!</div>
                )}
                {grouped.map(item => {
                  if (item.type === "day") {
                    return (
                      <div key={item.key} className="flex justify-center my-3">
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num bg-slate-900/60 border border-slate-800 rounded-full px-3 py-0.5">
                          {fmtDayLabel(item.iso)}
                        </span>
                      </div>
                    );
                  }
                  const m = item.msg;
                  const mine = m.sender_role !== "autista"; // office side
                  return (
                    <div key={item.key} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "bg-amber-500 text-slate-950" : "bg-slate-800 text-slate-100 border border-slate-700"}`}>
                        {!mine && (
                          <div className="text-[10px] font-mono-num uppercase tracking-widest opacity-70 mb-0.5">{m.sender_name || selected.driver_name}</div>
                        )}
                        <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{m.body}</div>
                        <div className={`flex items-center gap-1 justify-end mt-1 text-[10px] font-mono-num ${mine ? "text-slate-900/70" : "text-slate-500"}`}>
                          {fmtTime(m.created_at)}
                          {mine && (m.read_by_driver ? <CheckCheck size={12} /> : <Check size={12} />)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Composer */}
              <div className="p-3 border-t border-slate-800/80 bg-[#0A1428]">
                <div className="flex items-end gap-2">
                  <textarea
                    value={body}
                    onChange={e => setBody(e.target.value)}
                    onKeyDown={onKey}
                    rows={2}
                    placeholder="Scrivi un messaggio... (Invio per inviare, Shift+Invio per andare a capo)"
                    data-testid="chat-composer"
                    className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-amber-500/60 resize-none"
                  />
                  <Button
                    onClick={send}
                    disabled={sending || !body.trim()}
                    data-testid="chat-send-btn"
                    className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-950 font-bold gap-1.5 h-11 px-4"
                  >
                    <Send size={14} /> Invia
                  </Button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
